import { useContext, useState } from 'react';
import i18nStringsContext from '@contexts/i18nStringsContext';
import useLocaleUrl from '@hooks/use-locale-url';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { RootState } from '@shared/store';
import Heading from '@components/Heading';
import Link from '@components/Link';
import Button from '@components/Button';
import { Languages } from '@shared/types';
import styles from './ProductPricing.module.css';
import { Features } from '@shared/types/cancellation';
import { ProductFeatureList } from '@components/ProductFeatureList/ProductFeatureList';
import classNames from 'classnames';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NextLink from 'next/link';

const {
  Container,
  GrayBackground,
  ProductContainer,
  ProductName,
  PriceWrapper,
  Price: PriceClass,
  FlexContainer,
  PriceDecimal,
  PriceIntervalBusiness,
  PriceInterval,
  IsPopularLabel,
  FeatureContainer,
  Description,
  BelowPrice,
  VatText,
  LinkStyle,
  ReadMoreLink,
  SubmitButton,
  IsPopularClass,
  ProductFeaturesContainer,
  ExpandIcon,
  DescriptionContainer,
  DescriptionText,
  ProductFeatureItems,
  DescriptionSpacing,
} = styles;

export interface ProductPricingProps {
  id: string;
  name: string;
  isBusiness: boolean;
  isSingleWash: boolean;
  singleWashPrice: number;
  price: number;
  isPopular: boolean;
  description: string;
  features: Features[];
  productLink: string;
  isCampaign: boolean;
  campaignPrice: number;
  isMobile?: boolean;
}

const ProductPricing: React.FC<ProductPricingProps> = ({
  id,
  name,
  isBusiness,
  isSingleWash,
  singleWashPrice,
  price,
  isPopular,
  description,
  features,
  productLink,
  isCampaign,
  campaignPrice,
  isMobile = false,
}) => {
  const { locale } = useRouter();

  const isGermanWebsite = locale === Languages.DE.toLocaleLowerCase();
  const hasFeatures = Array.isArray(features) && features.length > 0;

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const {
    readMore: readMoreString,
    becomeMember: becomeMemberString,
    exclusiveVats: exclusiveVatsString,
    orTryASinglewash: orTryASinglewashString,
    singleWashLink: singleWashLinkString,
    isPopular: isPopularString,
    monthlyCurrencyShorthand,
    orTryUnlimitedWash: orTryUnlimitedWashString,
    unlimitedLinkText,
    signupForAmount: signupForAmountString,
  } = useContext(i18nStringsContext);

  const localeUrl = useLocaleUrl();

  const { options } = useSelector((state: RootState) => state.wordpress);

  const { singleWashLink, unlimitedWashLink, currencySymbol } = options || {};

  const floorPrice = Math.floor(price);
  const formatDecimal = price.toFixed(2);
  const decimal = formatDecimal.toString().slice(-2);

  const floorPriceSingleWash = Math.floor(singleWashPrice);
  const formatDecimalSingleWash = singleWashPrice?.toFixed(2);
  const decimalSingleWash = formatDecimalSingleWash?.toString().slice(-2);

  return (
    <div className={classNames(Container, { [IsPopularClass]: isPopular })}>
      {isPopular && isPopularString && (
        <div className={IsPopularLabel}>{isPopularString.toUpperCase()}</div>
      )}

      <div
        className={`${ProductContainer} ${isBusiness ? GrayBackground : ''}`}
      >
        <Heading element="h5" className={ProductName} hasHTML={false}>
          {name}
        </Heading>

        <div className={PriceWrapper}>
          <span className={PriceClass}>
            {isSingleWash ? floorPriceSingleWash : floorPrice}
          </span>

          {isBusiness || isGermanWebsite ? (
            <div className={FlexContainer}>
              <span className={PriceDecimal}>
                {isSingleWash ? decimalSingleWash : decimal}
              </span>

              <span className={PriceIntervalBusiness}>
                {!isSingleWash ? monthlyCurrencyShorthand : currencySymbol}
              </span>
            </div>
          ) : (
            <span className={PriceInterval}>
              {!isSingleWash ? monthlyCurrencyShorthand : currencySymbol}
            </span>
          )}
        </div>

        <div className={BelowPrice}>
          {isSingleWash
            ? unlimitedWashLink && (
                <span>
                  {orTryUnlimitedWashString
                    ?.replace('{unlimited}', '')
                    ?.replace(
                      '{price}',
                      `${
                        isBusiness
                          ? formatDecimal.replace('.', ',')
                          : price.toString().replace('.', ',')
                      } ${monthlyCurrencyShorthand}`
                    )}{' '}
                  <NextLink
                    href={`${unlimitedWashLink.url}?plan=${id}`}
                    className={LinkStyle}
                  >
                    {unlimitedLinkText}
                  </NextLink>
                </span>
              )
            : singleWashLink && (
                <span>
                  {orTryASinglewashString?.replace('{singleWashLink}', '')}

                  <NextLink
                    href={`${singleWashLink?.url}?plan=${id}`}
                    className={LinkStyle}
                  >
                    {singleWashLinkString?.replace(
                      '{price}',
                      isBusiness || isGermanWebsite
                        ? formatDecimalSingleWash.replace('.', ',')
                        : String(singleWashPrice)
                    )}
                  </NextLink>
                </span>
              )}
        </div>

        {isBusiness && <span className={VatText}>{exclusiveVatsString}</span>}

        {isMobile ? (
          <div
            className={ProductFeaturesContainer}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Accordion
              expanded={isExpanded}
              onChange={() => setIsExpanded(!isExpanded)}
              sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}
            >
              <AccordionSummary
                expandIcon={
                  isExpanded ? (
                    <ExpandLessIcon className={ExpandIcon} />
                  ) : (
                    <ExpandMoreIcon className={ExpandIcon} />
                  )
                }
                className={classNames(DescriptionContainer, {
                  [DescriptionSpacing]: !isSingleWash,
                })}
                sx={{
                  pb: '0 !important',
                  '.Mui-expanded': { transform: 'none !important', margin: 0 },
                  '.MuiAccordionSummary-content': {
                    margin: 0,
                    justifyContent: 'center',
                  },
                }}
              >
                <p className={DescriptionText}>{description || ''}</p>
              </AccordionSummary>

              {features && (
                <AccordionDetails className={ProductFeatureItems} sx={{ p: 0 }}>
                  <ProductFeatureList
                    features={features}
                    isBusiness={isBusiness}
                  />
                </AccordionDetails>
              )}
            </Accordion>
          </div>
        ) : (
          <div className={FeatureContainer}>
            <span className={Description}>{description}</span>

            {hasFeatures && (
              <ProductFeatureList features={features} isBusiness={isBusiness} />
            )}
          </div>
        )}

        {!isSingleWash && (
          <Button
            appearance="text"
            element={Link}
            className={ReadMoreLink}
            href={productLink}
          >
            {readMoreString}
          </Button>
        )}

        <Button
          element={Link}
          appearance="primary"
          hasArrow={false}
          href={
            !isSingleWash
              ? localeUrl(
                  `/checkout?plan=${id}&single=0${
                    isBusiness ? '&business=1' : ''
                  }`
                )
              : productLink
          }
          className={SubmitButton}
          id={
            isSingleWash
              ? `pricelist-find-washhall-${id}`
              : `pricelist-become-member-${id}`
          }
        >
          {!isCampaign && (isSingleWash ? readMoreString : becomeMemberString)}

          {isCampaign &&
            (signupForAmountString || '').replace(
              '{amount}',
              String(campaignPrice) || '0'
            )}
        </Button>
      </div>
    </div>
  );
};

export default ProductPricing;
